import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import OptimalRoutesMap from '../components/OptimalRoutesMap';
import { toast } from 'react-toastify';
import { useWebSocket } from '../contexts/WebSocketContext';
import { fetchOptimalRoutes, saveDeliveryPlan, addShipmentsToRoute, updateTelegramBot, addShipments, createCustomShipment} from '../utils/api';
import { getDriverColor } from '../utils/routeUtils';

export default function OptimalRoutesScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addListener } = useWebSocket();
  
  // Get delivery date and number of drivers from URL parameters
  const deliveryDate = searchParams.get('delivery_date');
  const iterationId = searchParams.get('iteration_id');
  
  const [routes, setRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCalculatingTimeFrames, setIsCalculatingTimeFrames] = useState(false);
  const [webhookCleanup, setWebhookCleanup] = useState(null);
  const [isSavingPlan, setIsSavingPlan] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isWaypointsModalOpen, setIsWaypointsModalOpen] = useState(false);

  const [driverDistances, setDriverDistances] = useState({});
  
  // State for Add Shipments modal
  const [currentShipmentId, setCurrentShipmentId] = useState('');
  const [selectedDriver, setSelectedDriver] = useState('');
  const [sequence, setSequence] = useState('');
  const [addedShipmentIds, setAddedShipmentIds] = useState([]);
  const [isAddingShipments, setIsAddingShipments] = useState(false);

  // State for Add Waypoints modal
  const [currentWaypointName, setCurrentWaypointName] = useState('');
  const [waypointAddress, setWaypointAddress] = useState('');
  const [waypointDriver, setWaypointDriver] = useState('');
  const [waypointSequence, setWaypointSequence] = useState('');
  const [addedWaypoints, setAddedWaypoints] = useState([]);
  const [isAddingWaypoints, setIsAddingWaypoints] = useState(false);
  const [waypointLatitude, setWaypointLatitude] = useState(null);
  const [waypointLongitude, setWaypointLongitude] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);

  const [drivers, setDrivers] = useState([]);

  const [isUpdatingTelegramBot, setIsUpdatingTelegramBot] = useState(false);
  
  // New state for shipment search
  const [searchShipmentNumber, setSearchShipmentNumber] = useState('');
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSidebar, setShowSidebar] = useState(true);
  const mapRef = useRef(null);

  // Set up WebSocket listener for time frames calculation completion
  useEffect(() => {
    const handleWebSocketMessage = (data) => {
      console.log('WebSocket message in optimal routes screen:', data);
      
      // Check if this is a response to our time frames calculation request
      if (data.script === 'includeCustomerTimeArrive' && data.delivery_date === deliveryDate) {
        if (data.status === 'success') {
          console.log('Розрахунок часу прибуття для клієнтів успішно завершено:', data);
          toast.success('Розрахунок часу прибуття для клієнтів успішно завершено!');
          setIsCalculatingTimeFrames(false);
          
          // Reload the routes to get the updated time frames
          loadOptimalRoutes();
        } else if (data.status === 'error') {
          console.error('Розрахунок часу прибуття для клієнтів не вдався:', data.message || 'Unknown error');
          toast.error(`Розрахунок часу прибуття для клієнтів не вдався: ${data.message || 'Unknown error'}`);
          setIsCalculatingTimeFrames(false);
        }
      }
    };
    
    // Register the websocket listener
    if (addListener) {
      const removeListener = addListener(handleWebSocketMessage);
      setWebhookCleanup(() => removeListener);
    }
    
    // Cleanup function
    return () => {
      if (webhookCleanup) {
        webhookCleanup();
      }
    };
  }, [addListener, deliveryDate, iterationId]);

  // Load optimal routes when component mounts
  const loadOptimalRoutes = async () => {
    if (!deliveryDate) {
      setError('No delivery date specified');
      setIsLoading(false);
      return;
    }
    
    // Only show loading indicator on initial load, not on updates
    // This prevents the map from re-centering due to the loading state
    if (routes.length === 0) {
      setIsLoading(true);
    }
    
    try {
      // Directly fetch from the server endpoint
      const result = await fetchOptimalRoutes(deliveryDate, iterationId);
      
      console.log('Result:', result);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch optimal routes');
      }

      // Process the driver routes into a format suitable for the map
      const routesData = result.data;

      // Update routes in a way that preserves map state
      setRoutes(prevRoutes => {
        // If this is an update (not initial load), and we have a similar number
        // of routes, try to update without triggering a full map re-render
        if (prevRoutes.length > 0 && 
            Math.abs(prevRoutes.length - routesData.length) < 10) {
          console.log('Updating routes with minimal re-render');
          return [...routesData];
        }
        
        // Otherwise do a full update
        return routesData;
      });
    } catch (error) {
      console.error('Error loading optimal routes:', error);
      setError(`Failed to load optimal routes: ${error.message}`);
      toast.error(`Failed to load optimal routes: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const drivers = {};
    routes.forEach(route => {
      if (!drivers[route.driver_id]) {
        drivers[route.driver_id] = {
          id: route.driver_id,
          name: route.name,
          driverNumber: route.driver_id
        }
      }
    })

    setDrivers(Object.values(drivers));
    console.log('Drivers for the day:', Object.values(drivers));
  }, [routes]);

  // Initial load of optimal routes
  useEffect(() => {
    loadOptimalRoutes();
  }, [deliveryDate, iterationId]);

  // Function to handle Calculate Time Frames button click
  const handleCalculateTimeFrames = async () => {
    if (!deliveryDate) {
      toast.error('No delivery date specified');
      return;
    }
    
    setIsCalculatingTimeFrames(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_PYTHON_BACKEND_URL}/api/includeCustomerTimeArrive?delivery_date=${deliveryDate}&iteration_id=${iterationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to calculate time frames: ${errorText}`);
      }
      
      toast.success('Розрахунок часу прибуття для клієнтів успішно запущений');
      // No navigation needed, just wait for the webhook response
    } catch (error) {
      console.error('Error calculating time frames:', error);
      toast.error(`Failed to calculate time frames: ${error.message}`);
      setIsCalculatingTimeFrames(false);
    }
  };

  // New function to handle saving the plan
  const handleSavePlan = async () => {
    if (!deliveryDate || !iterationId) {
      toast.error('Missing delivery date or iteration ID');
      return;
    }
    
    setIsSavingPlan(true);
    
    try {
      // Save the delivery plan
      const result = await saveDeliveryPlan(deliveryDate, iterationId);
      
      if (result.success) {
        toast.success('План доставки успішно збережений!');
      } else {
        throw new Error(result.error || 'Не вдалося зберегти план доставки');
      }
    } catch (error) {
      console.error('Помилка збереження плану доставки:', error);
      toast.error(`Не вдалося зберегти план доставки: ${error.message}`);
    } finally {
      setIsSavingPlan(false);
    }
  };

  // Function to calculate time frames and then save the plan
  const handleCalculateTimeFramesAndSave = async () => {
    if (!deliveryDate || !iterationId) {
      toast.error('Відсутня дата доставки або ітерація ID');
      return;
    }
    
    // First calculate time frames
    await handleCalculateTimeFrames();
    
    // After time frames calculation is done, save the plan
    // We'll wait for the webhook response before saving
    // This is handled in the WebSocket listener
  };

  // Close modal function
  const closeModal = () => {
    if (!isAddingShipments) {
      setIsModalOpen(false);
      setCurrentShipmentId('');
      setSelectedDriver('');
      setSequence('');
      setAddedShipmentIds([]);
    }
  };

  // Handle add to list
  const handleAddToList = () => {
    if (currentShipmentId.trim()) {
      // Avoid duplicates
      if (!addedShipmentIds.includes(currentShipmentId.trim())) {
        if (selectedDriver === '') {
          toast.error('Будь ласка, виберіть водія');
          return;
        }

        if (sequence === '') {
          toast.error('Будь ласка, введіть порядковий номер');
          return;
        }

        const newShipment = {
          id: currentShipmentId.trim(),
          driver: selectedDriver,
          sequence: sequence || '90'
        };
        setAddedShipmentIds([...addedShipmentIds, newShipment]);
      }
      // Reset input fields
      setCurrentShipmentId('');
      setSelectedDriver('');
      setSequence('');
    }
  };

  // Handle key press for Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddToList();
    }
  };

  // Handle remove id
  const handleRemoveId = (idToRemove) => {
    setAddedShipmentIds(addedShipmentIds.filter(shipment => shipment.id !== idToRemove));
  };

  // Handle submit
  const handleSubmitAddShipments = async () => {
    if (addedShipmentIds.length > 0) {
      try {
        setIsAddingShipments(true);
        
        console.log('Added shipment ids:', JSON.stringify(addedShipmentIds, null, 2));
        
        try {
          const addResult = await addShipments(deliveryDate, addedShipmentIds.map(shipment => shipment.id)); 

          const result = await addShipmentsToRoute(addedShipmentIds, deliveryDate, iterationId);
          console.log('Result:', result);
          toast.success('Shipments added successfully');
          await loadOptimalRoutes();
          closeModal();
          setIsAddingShipments(false);
        } catch (e) {
          console.error('Error adding shipments:', e);
          toast.error('Failed to add shipments. Please try again.');
          setIsAddingShipments(false);
        } finally {
          setIsAddingShipments(false);
        }
      } catch (e) {
        console.error('Error adding shipments:', e);
        toast.error('Failed to add shipments. Please try again.');
        setIsAddingShipments(false);
      }
    }
  };

  const updateTelegramBotHandler = async () => {
    try {
      setIsUpdatingTelegramBot(true);
      const result = await updateTelegramBot(deliveryDate, iterationId);
      console.log('Telegram bot request update result:', result);
      toast.success('Telegram bot updated successfully for delivery date: ' + deliveryDate);
    } catch (e) {
      console.error('Error updating Telegram bot:', e);
      toast.error('Failed to update Telegram bot. Please try again.');
    } finally {
      setIsUpdatingTelegramBot(false);
    }
  }

  // Close Add Waypoints modal function
  const closeWaypointsModal = () => {
    if (!isAddingWaypoints) {
      setIsWaypointsModalOpen(false);
      setCurrentWaypointName('');
      setWaypointAddress('');
      setWaypointDriver('');
      setWaypointSequence('');
      setWaypointLatitude(null);
      setWaypointLongitude(null);
      setAddedWaypoints([]);
    }
  };

  // Initialize Google Places Autocomplete for waypoints modal
  useEffect(() => {
    if (isWaypointsModalOpen && window.google && window.google.maps && window.google.maps.places) {
      setTimeout(() => {
        const addressInput = document.getElementById('waypoint-address-input');
        if (addressInput) {
          const autocompleteInstance = new window.google.maps.places.Autocomplete(addressInput);
          setAutocomplete(autocompleteInstance);

          // Add place_changed event listener
          autocompleteInstance.addListener('place_changed', () => {
            const place = autocompleteInstance.getPlace();
            if (place.geometry) {
              const lat = place.geometry.location.lat();
              const lng = place.geometry.location.lng();
              console.log('Place selected:', place.formatted_address);
              console.log('Latitude:', lat);
              console.log('Longitude:', lng);
              
              setWaypointAddress(place.formatted_address);
              setWaypointLatitude(lat);
              setWaypointLongitude(lng);
            }
          });
        }
      }, 100); // Small delay to ensure the DOM is ready
    }
  }, [isWaypointsModalOpen]);

  // Handle adding waypoint to list
  const handleAddWaypointToList = () => {
    if (currentWaypointName.trim() && waypointAddress.trim() && waypointLatitude && waypointLongitude) {
      // Avoid duplicates by name
      if (!addedWaypoints.some(wp => wp.name === currentWaypointName.trim())) {
        const newWaypoint = {
          name: currentWaypointName.trim(),
          address: waypointAddress.trim(),
          driver: waypointDriver || '',
          sequence: waypointSequence || '90',
          latitude: waypointLatitude,
          longitude: waypointLongitude
        };
        setAddedWaypoints([...addedWaypoints, newWaypoint]);
      }
      // Reset input fields
      setCurrentWaypointName('');
      setWaypointAddress('');
      setWaypointDriver('');
      setWaypointSequence('');
      setWaypointLatitude(null);
      setWaypointLongitude(null);
    } else {
      // Show error if coordinates are missing
      if (!waypointLatitude || !waypointLongitude) {
        toast.error('Будь ласка, виберіть адресу зі списку, щоб зафіксувати координати');
      } else {
        toast.error('Будь ласка, введіть назву зупинки і адресу');
      }
    }
  };

  // Handle key press for Enter key in waypoints modal
  const handleWaypointKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddWaypointToList();
    }
  };

  // Handle remove waypoint
  const handleRemoveWaypoint = (nameToRemove) => {
    setAddedWaypoints(addedWaypoints.filter(waypoint => waypoint.name !== nameToRemove));
  };

  // Handle submit waypoints
  const handleSubmitAddWaypoints = async () => {
    if (addedWaypoints.length > 0) {
      try {
        setIsAddingWaypoints(true);
        
        console.log('Added waypoints:', JSON.stringify(addedWaypoints, null, 2));
        
        try {
          console.log('Adding new waypoints ', JSON.stringify(addedWaypoints, null, 2));

          for (const waypoint of addedWaypoints) {
            try {
              const creatingWaypointResult = await createCustomShipment({
                name: waypoint.name,
                address: waypoint.address,
                latitude: waypoint.latitude,
                longitude: waypoint.longitude,
                delivery_date: deliveryDate
              });

              const addingWaypointResult = await addShipmentsToRoute([{
                id: waypoint.name,
                driver: waypoint.driver,
                sequence: waypoint.sequence
              }], deliveryDate, iterationId);

              console.log('Result:', creatingWaypointResult);
              console.log('Adding waypoint result:', addingWaypointResult);
            } catch (e) {
              console.error('Error creating waypoint:', e);
              toast.error(`Failed to create waypoint: ${e.message}`);
            }
          }

          toast.success('Зупинки додано успішно');
          await loadOptimalRoutes();
          closeWaypointsModal();
          setIsAddingWaypoints(false);
        } catch (e) {
          console.error('Error adding waypoints:', e);
          toast.error(`Не вдалося додати зупинки: ${e.message}`);
        } finally {
          setIsAddingWaypoints(false);
        }
      } catch (e) {
        console.error('Error adding waypoints:', e);
        toast.error(`Не вдалося додати зупинки: ${e.message}`);
        setIsAddingWaypoints(false);
      }
    }
  };

  // Function to handle shipment search
  const handleShipmentSearch = (e) => {
    e.preventDefault();
    
    if (!searchShipmentNumber || !routes.length) return;
    
    // Reset previous search results
    setSelectedShipment(null);
    setSearchResults([]);
    
    const searchText = searchShipmentNumber.trim().toLowerCase();
    
    // Search in all routes for shipments containing the search text
    const foundShipments = routes.filter(route => 
      route.shipment_name?.toLowerCase().includes(searchText)
    );
    
    if (foundShipments.length > 0) {
      setSearchResults(foundShipments);
      
      // Show success toast with result count
      if (foundShipments.length === 1) {
        toast.success(`Found shipment ${foundShipments[0].shipment_name}`);
      } else {
        toast.success(`Found ${foundShipments.length} shipments matching "${searchText}"`);
      }
    } else {
      toast.error(`No shipments found containing "${searchText}"`);
    }
  };
  
  // Function to select a specific shipment from search results
  const handleSelectShipment = (shipment) => {
    setSelectedShipment(shipment);
    toast.info(`Selected shipment ${shipment.shipment_name}`);
  };

  // Add a toggle for the sidebar
  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const navigationButtons = (
    <>
      <button
        onClick={() => navigate('/')}
        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        Додому
      </button>
      <button
        onClick={handleCalculateTimeFramesAndSave}
        disabled={isCalculatingTimeFrames || isSavingPlan}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300 ml-2"
      >
        {isCalculatingTimeFrames ? 'Розраховуємо...' : isSavingPlan ? 'Зберігаємо...' : 'Розрахувати час прибуття для клієнтів'}
      </button>
      <button
        onClick={handleSavePlan}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ml-2"
      >
        Зберегти на домашню сторінку
      </button>
      <button
        onClick={() => setIsModalOpen(true)}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ml-2"
      >
        Додати посилки
      </button>
      <button
        onClick={() => setIsWaypointsModalOpen(true)}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ml-2"
      >
        Додати зупинки
      </button>
      <button
        onClick={updateTelegramBotHandler}
        disabled={isUpdatingTelegramBot}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ml-2 disabled:bg-indigo-400"
      >
        {isUpdatingTelegramBot ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Оновлюємо...
          </>
        ) : 'Оновити Telegram бот'}
      </button>
    </>
  );

  return (
    <div className="flex flex-col h-screen">
      <Navbar buttons={navigationButtons} />
      
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar toggle button (for small screens) */}
        <button 
          onClick={toggleSidebar}
          className="md:hidden absolute top-16 left-2 z-20 p-2 bg-white rounded-md shadow-md"
        >
          {showSidebar ? '←' : '→'}
        </button>
        
        {/* Left sidebar */}
        {showSidebar && (
          <div className="w-64 bg-white shadow-lg p-4 overflow-auto z-10">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Маршутний день</h2>
            
            {/* Search shipment form */}
            <div className="mt-4 mb-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Пошук посилки:</h3>
              <form onSubmit={handleShipmentSearch} className="flex flex-col space-y-2">
                <input
                  type="text"
                  value={searchShipmentNumber}
                  onChange={(e) => setSearchShipmentNumber(e.target.value)}
                  placeholder="Введіть SHNL... номер"
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
                <button
                  type="submit"
                  className="w-full px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Пошук
                </button>
              </form>
            </div>
            
            {/* Search results */}
            {searchResults.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Результати пошуку:</h3>
                <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-md">
                  <ul className="divide-y divide-gray-200">
                    {searchResults.map((shipment, index) => {
                      // Check if this is a pickup based on delivery_type
                      const isPickup = shipment?.delivery_type?.toLowerCase() === 'pickup' || shipment?.delivery_type?.toLowerCase() === 'unknown';
                      const address = isPickup ? shipment?.sender?.address?.string : shipment?.recipient?.address?.string;
                      const customerName = isPickup ? shipment?.sender?.name : shipment?.recipient?.name;
                      const phoneNumber = isPickup ? shipment?.sender?.phone : shipment?.recipient?.phone;
                      
                      return (
                        <li 
                          key={`${shipment.shipment_name}-${index}`} 
                          className={`p-3 hover:bg-gray-50 cursor-pointer ${selectedShipment?.shipment_name === shipment.shipment_name ? 'bg-indigo-50' : ''}`}
                          onClick={() => handleSelectShipment(shipment)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="text-sm font-medium text-indigo-600">{shipment.shipment_name}</div>
                            {shipment.sequence && (
                              <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded">
                                Stop #{shipment.sequence}
                              </span>
                            )}
                          </div>
                          
                          <div className="mt-1 space-y-1 text-xs">
                            <div className="text-gray-700">
                              <span className="font-medium">Driver:</span> {shipment.name || `Driver ${shipment.driver_id}`}
                            </div>
                            <div className="text-gray-700">
                              <span className="font-medium">Delivery Type:</span> {shipment.delivery_type || 'N/A'}
                            </div>
                            <div className="text-gray-700">
                              <span className="font-medium">Address:</span> {address || 'N/A'}
                            </div>
                            <div className="text-gray-700">
                              <span className="font-medium">Customer:</span> {customerName || 'N/A'}
                            </div>
                            <div className="text-gray-700">
                              <span className="font-medium">Phone:</span> {phoneNumber || 'N/A'}
                            </div>
                            
                            {shipment.weight > 0 && (
                              <div className="text-gray-700">
                                <span className="font-medium">Weight:</span> {shipment.weight}
                              </div>
                            )}
                            
                            {shipment.volume > 0 && (
                              <div className="text-gray-700">
                                <span className="font-medium">Volume:</span> {shipment.volume}
                              </div>
                            )}
                            
                            {shipment.driver_status && (
                              <div className="text-gray-700">
                                <span className="font-medium">Status:</span> {
                                  shipment.driver_status === 'Delivered' 
                                    ? <span>Delivered ✅</span> 
                                    : shipment.driver_status === 'Not Delivered'
                                      ? <span>Not Delivered ❌</span>
                                      : shipment.driver_status === 'Skipped'
                                        ? <span>Skipped ⏭️</span>
                                        : <span>{shipment.driver_status}</span>
                                }
                              </div>
                            )}

                            {shipment.driver_comment && (
                              <div className="text-gray-700">
                                <span className="font-medium">Comment:</span> {shipment.driver_comment}
                              </div>
                            )}
                            
                            {shipment.arrival_time && (
                              <div className="text-gray-700">
                                <span className="font-medium">Expected arrival:</span> {new Date(shipment.arrival_time).toLocaleString()}
                              </div>
                            )}
                            
                            {shipment.driver_delivered_at && (
                              <div className="text-gray-700">
                                <span className="font-medium">Delivered at:</span> {new Date(shipment.driver_delivered_at).toLocaleString()}
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            )}
            
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Доставочний день:</h3>
              <p className="text-sm text-gray-600">
                {deliveryDate ? new Date(deliveryDate).toLocaleDateString('uk-UA', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : 'Дату не обрано'}
              </p>
            </div>
            
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Доступні водії:</h3>
              <div className="space-y-1">
                {drivers.length > 0 ? (
                  drivers.map((driver, index) => (
                    <div key={driver.id} className="text-sm text-gray-600 flex items-center">
                      <div 
                        className="h-3 w-3 rounded-full mr-2" 
                        style={{ backgroundColor: getDriverColor(driver.driverNumber) }}
                      ></div>
                      {driver.name}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 italic">Доступних водіїв немає</p>
                )}
              </div>
            </div>

            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Відстані водіїв:</h3>
              <div className="space-y-1">
                {Object.keys(driverDistances).length > 0 ? (
                  <>
                    {Object.entries(driverDistances).map(([driverName, distance]) => {
                      const driver = drivers.find(d => d.name === driverName);
                      const driverNumber = driver ? driver.driverNumber : null;
                      
                      return (
                        <div key={driverName} className="text-sm text-gray-600 flex items-center justify-between">
                          <div className="flex items-center">
                            <div 
                              className="h-3 w-3 rounded-full mr-2" 
                              style={{ backgroundColor: getDriverColor(driverNumber) }}
                            ></div>
                            <span>{driverName}</span>
                          </div>
                          <span className="font-medium">{distance} km</span>
                        </div>
                      );
                    })}
                    
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <div className="text-sm font-medium text-gray-800 flex items-center justify-between">
                        <span>Загальна відстань:</span>
                        <span>
                          {Object.values(driverDistances).reduce((sum, distance) => sum + distance, 0).toFixed(0)} km
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 italic">No distance data available</p>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Map area */}
        <div className={`flex-1 relative ${showSidebar ? 'md:ml-0' : ''}`}>
          {isLoading ? (
            <div className="absolute inset-0 bg-gray-50 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : error ? (
            <div className="absolute inset-0 bg-gray-50 flex items-center justify-center">
              <div className="bg-white p-6 rounded-lg shadow max-w-md">
                <h3 className="text-red-600 text-lg font-semibold mb-2">Error</h3>
                <p className="text-gray-700">{error}</p>
              </div>
            </div>
          ) : (
            <OptimalRoutesMap
              routes={routes}
              deliveryDate={deliveryDate}
              iterationId={iterationId}
              loadOptimalRoutes={loadOptimalRoutes}
              selectedShipment={selectedShipment}
              setDriverDistances={setDriverDistances}
            />
          )}
        </div>
      </div>

      {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[1000] flex items-center justify-center p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative z-[1001]">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Додати посилки</h3>
              
              {isAddingShipments ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
                  <p className="text-gray-600">Додаємо посилки до доставочного дня...</p>
                </div>
              ) : (
                <>
                  {/* Input fields */}
                  <div className="mb-4">
                    <label htmlFor="shipmentId" className="block text-sm font-medium text-gray-700 mb-2">
                      Номер посилки
                    </label>
                    <input
                      type="text"
                      id="shipmentId"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      value={currentShipmentId}
                      onChange={(e) => setCurrentShipmentId(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Введіть SHNL... номер"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="driverSelect" className="block text-sm font-medium text-gray-700 mb-2">
                        Водій
                      </label>
                      <select
                        id="driverSelect"
                        value={selectedDriver}
                        onChange={(e) => setSelectedDriver(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Виберіть водія</option>
                        {drivers.map(driver => (
                          <option key={driver.id} value={driver.id}>{driver.name}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="sequence" className="block text-sm font-medium text-gray-700 mb-2">
                        Порядок в маршруті
                      </label>
                      <input
                        type="number"
                        id="sequence"
                        min="1"
                        value={sequence}
                        onChange={(e) => setSequence(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Порядковий номер в маршруті</p>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <button
                      type="button"
                      onClick={handleAddToList}
                      className="w-full px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Додати посилку
                    </button>
                  </div>

                  {/* List of added IDs */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Посилки для додавання:
                    </label>
                    {addedShipmentIds.length > 0 ? (
                      <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                        <ul className="divide-y divide-gray-200">
                          {addedShipmentIds.map((shipment, index) => (
                            <li key={index} className="py-2 flex justify-between items-center">
                              <div>
                                <span className="text-sm text-gray-800 font-medium">{shipment.id}</span>
                                <span className="text-xs text-gray-500 ml-2">
                                  (Driver: {shipment.driver}, Sequence: {shipment.sequence})
                                </span>
                              </div>
                              <button
                                onClick={() => handleRemoveId(shipment.id)}
                                className="text-red-500 hover:text-red-700"
                                title="Remove"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className="text-gray-500 italic border border-gray-300 rounded-md p-4 text-center">
                        Посилки для додавання немає
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Скасувати
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmitAddShipments}
                      disabled={addedShipmentIds.length === 0}
                      className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                        addedShipmentIds.length === 0 
                          ? 'bg-indigo-400 cursor-not-allowed' 
                          : 'bg-indigo-600 hover:bg-indigo-700'
                      }`}
                    >
                      Надіслати ({addedShipmentIds.length})
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Add Waypoints Modal */}
        {isWaypointsModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[1000] flex items-center justify-center p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative z-[1001]">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Додати зупинки</h3>
              
              {isAddingWaypoints ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
                  <p className="text-gray-600">Додаємо зупинки до маршруту...</p>
                </div>
              ) : (
                <>
                  {/* Input fields */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Назва зупинки</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Введіть назву зупинки (не має повторюватися!)"
                      value={currentWaypointName}
                      onChange={(e) => setCurrentWaypointName(e.target.value)}
                      onKeyPress={handleWaypointKeyPress}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Адреса</label>
                    <input
                      id="waypoint-address-input"
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Введіть адресу і обов'язково виберіть зі списку"
                      value={waypointAddress}
                      onChange={(e) => setWaypointAddress(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && e.preventDefault()}
                    />
                    {waypointLatitude && waypointLongitude ? (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ Координати зафіксовані: {waypointLatitude.toFixed(6)}, {waypointLongitude.toFixed(6)}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">
                        Будь ласка, виберіть адресу зі списку, щоб зафіксувати координати
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="waypointDriverSelect" className="block text-sm font-medium text-gray-700 mb-2">
                        Водій
                      </label>
                      <select
                        id="waypointDriverSelect"
                        value={waypointDriver}
                        onChange={(e) => setWaypointDriver(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Виберіть водія</option>
                        {drivers.map(driver => (
                          <option key={driver.id} value={driver.id}>{driver.name}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="waypointSequence" className="block text-sm font-medium text-gray-700 mb-2">
                        Порядок в маршруті
                      </label>
                      <input
                        type="number"
                        id="waypointSequence"
                        min="1"
                        value={waypointSequence}
                        onChange={(e) => setWaypointSequence(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Порядковий номер в маршруті</p>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <button
                      type="button"
                      onClick={handleAddWaypointToList}
                      className="w-full px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Додати зупинку
                    </button>
                  </div>

                  {/* List of added waypoints */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Зупинки для додавання:
                    </label>
                    {addedWaypoints.length > 0 ? (
                      <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                        <ul className="divide-y divide-gray-200">
                          {addedWaypoints.map((waypoint, index) => (
                            <li key={index} className="py-2 flex justify-between items-center">
                              <div>
                                <span className="text-sm text-gray-800 font-medium">{waypoint.name}</span>
                                <span className="text-xs text-gray-500 ml-2">
                                  {waypoint.address.length > 30 ? waypoint.address.substring(0, 30) + '...' : waypoint.address}
                                </span>
                                <span className="text-xs text-gray-500 ml-2">
                                  (Driver: {waypoint.driver || 'Not selected'}, Sequence: {waypoint.sequence})
                                </span>
                              </div>
                              <button
                                onClick={() => handleRemoveWaypoint(waypoint.name)}
                                className="text-red-500 hover:text-red-700"
                                title="Remove"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className="text-gray-500 italic border border-gray-300 rounded-md p-4 text-center">
                        Зупинки для додавання немає
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      type="button"
                      onClick={closeWaypointsModal}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Скасувати
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmitAddWaypoints}
                      disabled={addedWaypoints.length === 0}
                      className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                        addedWaypoints.length === 0 
                          ? 'bg-indigo-400 cursor-not-allowed' 
                          : 'bg-indigo-600 hover:bg-indigo-700'
                      }`}
                    >
                      Надіслати ({addedWaypoints.length})
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      
      {/* Existing modal code... */}
    </div>
  );
} 